import {
  CopyObjectCommand,
  DeleteObjectCommand,
  GetObjectCommand,
  HeadObjectCommand,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { Readable } from 'node:stream';

export type StoredObjectHead = {
  sizeBytes: number;
  contentType: string | null;
  checksumSha256: string | null;
};

export interface StorageProvider {
  head(bucket: string, key: string): Promise<StoredObjectHead | null>;
  put(
    bucket: string,
    key: string,
    body: Uint8Array | Readable,
    options: {
      contentType: string;
      contentDisposition?: string;
      serverSideEncryption?: 'AES256' | 'aws:kms';
      contentLength?: number;
      checksumSha256?: string;
      immutable?: boolean;
    },
  ): Promise<void>;
  presignGet(
    bucket: string,
    key: string,
    options: { expiresInSeconds: number; responseContentType?: string; responseContentDisposition?: string },
  ): Promise<string>;
  readPrefix(bucket: string, key: string, maxBytes: number): Promise<Uint8Array>;
  download(
    bucket: string,
    key: string,
    options: { maxBytes: number; timeoutMs: number },
  ): Promise<Readable>;
  copy(sourceBucket: string, sourceKey: string, destinationBucket: string, destinationKey: string): Promise<void>;
  delete(bucket: string, key: string): Promise<void>;
}

export class S3StorageProvider implements StorageProvider {
  constructor(
    private readonly client: S3Client,
    private readonly presignClient: S3Client = client,
  ) {}

  async head(bucket: string, key: string): Promise<StoredObjectHead | null> {
    try {
      const result = await this.client.send(new HeadObjectCommand({ Bucket: bucket, Key: key }));
      return {
        sizeBytes: result.ContentLength ?? -1,
        contentType: result.ContentType?.split(';')[0]?.trim().toLowerCase() ?? null,
        checksumSha256: result.Metadata?.['loamify-sha256']?.toLowerCase() ?? null,
      };
    } catch (error) {
      const status = (error as { $metadata?: { httpStatusCode?: number } }).$metadata?.httpStatusCode;
      if (status === 404) return null;
      throw error;
    }
  }

  async put(
    bucket: string,
    key: string,
    body: Uint8Array | Readable,
    options: {
      contentType: string;
      contentDisposition?: string;
      serverSideEncryption?: 'AES256' | 'aws:kms';
      contentLength?: number;
      checksumSha256?: string;
      immutable?: boolean;
    },
  ) {
    await this.client.send(
      new PutObjectCommand({
        Bucket: bucket,
        Key: key,
        Body: body,
        ContentLength: options.contentLength,
        ContentType: options.contentType,
        ContentDisposition: options.contentDisposition,
        ServerSideEncryption: options.serverSideEncryption,
        ChecksumSHA256: options.checksumSha256
          ? Buffer.from(options.checksumSha256, 'hex').toString('base64')
          : undefined,
        Metadata: options.checksumSha256
          ? { 'loamify-sha256': options.checksumSha256.toLowerCase() }
          : undefined,
        IfNoneMatch: options.immutable ? '*' : undefined,
      }),
    );
  }

  presignGet(
    bucket: string,
    key: string,
    options: {
      expiresInSeconds: number;
      responseContentType?: string;
      responseContentDisposition?: string;
    },
  ) {
    return getSignedUrl(
      this.presignClient,
      new GetObjectCommand({
        Bucket: bucket,
        Key: key,
        ResponseContentType: options.responseContentType,
        ResponseContentDisposition: options.responseContentDisposition,
      }),
      { expiresIn: options.expiresInSeconds },
    );
  }

  async readPrefix(bucket: string, key: string, maxBytes: number): Promise<Uint8Array> {
    const response = await this.client.send(
      new GetObjectCommand({ Bucket: bucket, Key: key, Range: `bytes=0-${maxBytes - 1}` }),
    );
    if (!response.Body) throw new Error('Object body is empty');
    const bytes = await response.Body.transformToByteArray();
    return bytes.slice(0, maxBytes);
  }

  async download(
    bucket: string,
    key: string,
    options: { maxBytes: number; timeoutMs: number },
  ): Promise<Readable> {
    const head = await this.head(bucket, key);
    if (!head) throw new Error('Object not found');
    if (head.sizeBytes < 0 || head.sizeBytes > options.maxBytes) {
      throw new Error('Object exceeds download limit');
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs);
    try {
      const response = await this.client.send(
        new GetObjectCommand({ Bucket: bucket, Key: key }),
        { abortSignal: controller.signal },
      );
      if (!response.Body) throw new Error('Object body is empty');
      const body = response.Body as Readable;
      const streamTimer = setTimeout(
        () => body.destroy(new Error('Object download timed out')),
        options.timeoutMs,
      );
      const clearStreamTimer = () => clearTimeout(streamTimer);
      body.once('end', clearStreamTimer);
      body.once('close', clearStreamTimer);
      body.once('error', clearStreamTimer);
      return body;
    } finally {
      clearTimeout(timer);
    }
  }

  async copy(sourceBucket: string, sourceKey: string, destinationBucket: string, destinationKey: string) {
    await this.client.send(
      new CopyObjectCommand({
        Bucket: destinationBucket,
        Key: destinationKey,
        CopySource: `${encodeURIComponent(sourceBucket)}/${sourceKey.split('/').map(encodeURIComponent).join('/')}`,
      }),
    );
  }

  async delete(bucket: string, key: string) {
    await this.client.send(new DeleteObjectCommand({ Bucket: bucket, Key: key }));
  }
}
